<template>
  <div class="vendor-shell">
    <base-dialog :show="!!error" title="An error occurred!" @close="handleError">
      <p>{{ error }}</p>
    </base-dialog>
    <transition name="toast">
      <div
        v-if="baseToast.message"
        class="vendor-toast"
        :class="'is-' + (baseToast.type || 'info')"
      >
        <span class="dot"></span>
        <span>{{ baseToast.message }}</span>
      </div>
    </transition>

    <transition v-if="isPermission" name="fade" mode="out-in">
      <!-- Form view (create/update) — modern breadcrumb-style header, no wrapper card. -->
      <section key="form" v-if="isCreateUser" class="form-view">
        <div class="form-head">
          <button
            type="button"
            class="back-link"
            :disabled="isLoading"
            @click="backButtonClicked"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4">
              <path d="M15 18l-6-6 6-6" stroke-linecap="round" stroke-linejoin="round" />
            </svg>
            <span>Vendors</span>
          </button>
          <span class="head-sep">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6">
              <path d="M9 6l6 6-6 6" stroke-linecap="round" stroke-linejoin="round" />
            </svg>
          </span>
          <span
            class="head-chip"
            :class="mode === 'create' ? 'head-chip-new' : 'head-chip-edit'"
          >{{ mode === 'create' ? 'New' : 'Editing' }}</span>
          <span v-if="mode === 'update'" class="head-name">
            {{ selectedUser.name || selectedUser.username }}
            <span v-if="selectedUser.username" class="head-name-sub">@{{ selectedUser.username }}</span>
          </span>
          <span v-else class="head-name">Vendor</span>
        </div>

        <div v-if="isLoading" class="loading-block">
          <div class="loading-spinner"></div>
          <div class="loading-label">{{ mode === 'create' ? 'Creating…' : 'Updating…' }}</div>
        </div>
        <user-form
          v-else
          :mode="mode"
          :selected-user="selectedUser"
          @create-user="createUser"
        ></user-form>
      </section>

      <!-- List view -->
      <section key="list" v-else class="list-view">
        <header class="vendor-header">
          <div>
            <div class="eyebrow">Superadmin</div>
            <h1>Vendors</h1>
            <p class="subtitle">Manage vendor accounts, permissions, and status.</p>
          </div>
          <div class="header-actions">
            <button class="btn btn-primary" @click="createBtnClicked">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" class="btn-icon">
                <path d="M12 5v14M5 12h14" stroke-linecap="round" />
              </svg>
              New Vendor
            </button>
          </div>
        </header>

        <vendor-list
          @edit-user="editUser"
          @create-clicked="createBtnClicked"
          @toast="handleChildToast"
          @error="handleChildError"
        ></vendor-list>
      </section>
    </transition>

    <!-- Permission fallback (kept — preserves the "no access" screen behavior). -->
    <div v-else class="permission-block">
      <div class="permission-card">
        <div class="permission-icon">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6">
            <rect x="4" y="11" width="16" height="10" rx="2" />
            <path d="M8 11V7a4 4 0 018 0v4" />
          </svg>
        </div>
        <h2>You don't have permission to access this page</h2>
        <p>Head back to the dashboard.</p>
        <router-link to="/" class="btn btn-primary">Go to Home</router-link>
      </div>
    </div>
  </div>
</template>

<script>
import UserForm from "../../components/vendor/UserForm.vue";
import VendorList from "./vendor_list.vue";

export default {
  name: "vendors",
  components: { UserForm, VendorList },
  data() {
    return {
      isPermission: true,
      isLoading: false,
      error: null,
      isCreateUser: false,
      baseToast: { type: "", message: "" },
      mode: "create",
      selectedUser: Object,
    };
  },
  methods: {
    handleError() { this.error = null; },
    handleBaseToast() { this.baseToast = { type: "", message: "" }; },
    handleChildToast(payload) {
      // From vendor_list → status change success.
      this.baseToast = { type: payload.type || "info", message: payload.message || "" };
      setTimeout(() => this.handleBaseToast(), 2000);
    },
    handleChildError(err) {
      // From vendor_list → getUsers fail bubbles here so we can flip
      // the permission gate (403/401 shows the fallback screen).
      if (err && !err.status) this.isPermission = false;
      this.error = (err && err.message) || "Something failed!";
    },
    backButtonClicked() {
      this.isCreateUser = false;
      this.mode = "create";
      this.selectedUser = Object;
    },
    createBtnClicked() {
      this.mode = "create";
      this.isCreateUser = true;
      this.selectedUser = Object;
    },
    editUser(user) {
      this.mode = "update";
      this.isCreateUser = true;
      this.selectedUser = user;
    },
    async createUser(data) {
      this.isLoading = true;
      try {
        if (this.mode === "create") {
          await this.$store.dispatch("vendor/createUser", data);
          this.baseToast.message = "Vendor created successfully!";
        } else {
          await this.$store.dispatch("vendor/updateUser", data);
          this.baseToast.message = "Vendor updated successfully!";
        }
        // Re-fetch so the list mirrors what actually persisted rather than
        // the optimistic local commit — vendor_list reads the shared
        // vendor/users getter, so this updates it reactively.
        await this.$store.dispatch("vendor/getUsers");
        this.isCreateUser = false;
        this.baseToast.type = "success";
        setTimeout(() => this.handleBaseToast(), 1200);
      } catch (error) {
        this.error = error.message || "Something failed!";
      }
      this.isLoading = false;
    },
  },
};
</script>

<style scoped>
/* ------------- Shell ------------- */
.vendor-shell {
  height: 100%;
  display: flex;
  flex-direction: column;
  padding: 24px;
  gap: 20px;
  background: linear-gradient(180deg, #fbfaf7 0%, #f4f0e5 100%);
  overflow-y: auto;
}
.list-view, .form-view {
  display: flex;
  flex-direction: column;
  gap: 20px;
  flex: 1;
  min-height: 0;
}

/* ------------- List header ------------- */
.vendor-header {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
}
.eyebrow {
  font-size: 11px;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: #1B2A47;
  font-weight: 600;
  margin-bottom: 4px;
}
.vendor-header h1 {
  font-size: 28px;
  font-weight: 700;
  color: #0f172a;
  line-height: 1.1;
  margin: 0;
}
.subtitle {
  font-size: 13px;
  color: #64748b;
  margin: 4px 0 0;
}

/* ------------- Form head (breadcrumb inline) ------------- */
.form-head {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 2px 4px;
  flex-wrap: wrap;
  min-height: 32px;
}
.back-link {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 10px 6px 6px;
  border: none;
  background: transparent;
  color: #475569;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  border-radius: 8px;
  transition: color 0.12s, background 0.12s;
}
.back-link:disabled { opacity: 0.5; cursor: not-allowed; }
.back-link:hover:not(:disabled) {
  color: #1B2A47;
  background: #eef2f7;
}
.back-link svg { width: 16px; height: 16px; }

.head-sep {
  color: #cbd5e1;
  display: inline-flex;
  align-items: center;
}
.head-sep svg { width: 14px; height: 14px; }

.head-chip {
  display: inline-block;
  padding: 3px 10px;
  border-radius: 999px;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
}
.head-chip-new {
  background: linear-gradient(135deg, #1B2A47 0%, #2C3E63 100%);
  color: #fff;
  box-shadow: 0 4px 10px -4px rgba(27, 42, 71, 0.45);
}
.head-chip-edit {
  background: #ecfdf5;
  color: #047857;
  border: 1px solid #a7f3d0;
}

.head-name {
  font-size: 16px;
  font-weight: 700;
  color: #0f172a;
  letter-spacing: -0.01em;
  display: inline-flex;
  align-items: baseline;
  gap: 8px;
  min-width: 0;
}
.head-name-sub {
  font-size: 12px;
  font-weight: 500;
  color: #94a3b8;
  letter-spacing: 0;
  text-transform: none;
}

/* ------------- Buttons ------------- */
.btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  border-radius: 10px;
  font-weight: 600;
  font-size: 13px;
  cursor: pointer;
  border: 1px solid transparent;
  transition: all 0.15s ease;
  outline: none;
  text-decoration: none;
}
.btn-primary {
  background: linear-gradient(135deg, #1B2A47 0%, #2C3E63 100%);
  color: #fff;
  box-shadow: 0 6px 16px -6px rgba(27, 42, 71, 0.5);
}
.btn-primary:hover {
  transform: translateY(-1px);
  box-shadow: 0 10px 24px -8px rgba(27, 42, 71, 0.55);
}
.btn-icon { width: 15px; height: 15px; }

/* ------------- Loading ------------- */
.loading-block {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 14px;
  padding: 60px 20px;
}
.loading-spinner {
  width: 44px; height: 44px;
  border-radius: 999px;
  border: 3px solid #e2e8f0;
  border-top-color: #1B2A47;
  animation: spin 0.9s linear infinite;
}
@keyframes spin { to { transform: rotate(360deg); } }
.loading-label {
  font-size: 14px;
  font-weight: 600;
  color: #475569;
}

/* ------------- Permission fallback ------------- */
.permission-block {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
}
.permission-card {
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 20px;
  padding: 40px 32px;
  max-width: 420px;
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  box-shadow: 0 12px 30px -20px rgba(15, 23, 42, 0.25);
}
.permission-icon {
  width: 56px; height: 56px;
  border-radius: 14px;
  background: #fef2f2;
  color: #dc2626;
  display: flex; align-items: center; justify-content: center;
}
.permission-icon svg { width: 28px; height: 28px; }
.permission-card h2 {
  font-size: 18px;
  font-weight: 700;
  color: #0f172a;
  margin: 0;
  line-height: 1.3;
}
.permission-card p {
  font-size: 13px;
  color: #64748b;
  margin: 0 0 8px;
}

/* ------------- Toast ------------- */
.vendor-toast {
  position: fixed;
  top: 24px; right: 24px;
  z-index: 60;
  background: #0f172a; color: #fff;
  padding: 12px 18px;
  border-radius: 12px;
  font-size: 13px; font-weight: 500;
  display: inline-flex; align-items: center; gap: 10px;
  box-shadow: 0 12px 30px -10px rgba(15, 23, 42, 0.4);
}
.vendor-toast .dot {
  width: 8px; height: 8px; border-radius: 999px; background: #94a3b8;
}
.vendor-toast.is-success { background: #059669; }
.vendor-toast.is-success .dot { background: #a7f3d0; }
.vendor-toast.is-error { background: #dc2626; }
.vendor-toast.is-error .dot { background: #fecaca; }

/* ------------- Transitions ------------- */
.toast-enter-active, .toast-leave-active { transition: all 0.24s cubic-bezier(0.4, 0, 0.2, 1); }
.toast-enter-from, .toast-leave-to { transform: translateY(-12px); opacity: 0; }
.fade-enter-active, .fade-leave-active { transition: opacity 0.24s ease; }
.fade-enter-from, .fade-leave-to { opacity: 0; }
</style>
